import { Injectable } from '@angular/core';
import { Observable, catchError, map, of, shareReplay, switchMap } from 'rxjs';
import { PortalAuthService } from '../auth/portal-auth.service';
import { PortalTenantBrand } from '../domain/portal-tenant.model';
import { TenantApiService } from './tenant-api.service';

/**
 * Shared, cached lookup for the currently active tenant (name + logo). Both the shell
 * toolbar and the side-menu's tenant identity block need this; without a shared cache
 * each would fire its own request. Re-fetches whenever hasTenant$ changes
 * (login, tenant switch, admin act-as toggle). A failed lookup resolves to null rather
 * than erroring the stream, so callers just fall back to their own default display.
 *
 * Reads the brand endpoint, which every member may call. The full tenant (`GET /tenants/me`)
 * is owner-only, so using it here answered each invited Employee with a 403 on every page load.
 */
@Injectable({ providedIn: 'root' })
export class CurrentTenantService {
  private readonly brand$: Observable<PortalTenantBrand | null> = this.portalAuth.hasTenant$.pipe(
    switchMap(hasTenant => hasTenant
      ? this.tenantApi.getMyBrand().pipe(catchError(() => of(null)))
      : of(null)),
    shareReplay(1),
  );

  readonly name$: Observable<string | null> = this.brand$.pipe(map(b => b?.name ?? null));

  readonly logoUrl$: Observable<string | null> = this.brand$.pipe(map(b => b?.logoUrl ?? null));

  constructor(
    private readonly portalAuth: PortalAuthService,
    private readonly tenantApi: TenantApiService,
  ) {}
}
